import type * as z from "zod";
import type { pushBranchToLiveSchema } from "../../shared/mutators/pushBranchToLive";
import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { sharedMutators } from "../../shared/mutators";
import { useToast } from "../components/Toast";
import { executePushLive, type PushLiveBranch } from "../lib/executePushLive";
import { playPushLive } from "../lib/sounds";
import { navigate, routes } from "../lib/urls";

export function usePushLive({
  branchId,
  spaceId,
  onAccept,
  onDismiss,
  onDeploying,
  onMerging,
  stayAfterPush,
}: {
  branchId: string;
  spaceId: string;
  stayAfterPush?: boolean;
  onAccept?: () => void;
  onDismiss?: () => void;
  onDeploying?: (v: boolean) => void;
  onMerging?: (v: boolean) => void;
}) {
  const z = useZero();
  const { showToast } = useToast();

  const mergeMain = () => {
    onMerging?.(true);
    const { server } = z.mutate(
      sharedMutators.mergeMainIntoBranch({ branchId })
    );
    void server.then(() => onMerging?.(false));
  };

  const pushLive = (
    branch: PushLiveBranch | undefined,
    mainBranchId: string | undefined,
    copyVolumesToLive = false,
    toolResult?: z.infer<typeof pushBranchToLiveSchema>["toolResult"]
  ) => {
    const result = executePushLive({
      branch,
      mainBranchId,
      onAccept,
      onDismiss,
      submitPush: (sourceBranchId, message) =>
        z.mutate(
          sharedMutators.pushBranchToLive({
            sourceBranchId,
            newCommitId: nanoid(12),
            newChatId: nanoid(12),
            message,
            copyVolumesToLive,
            toolResult,
          })
        ),
      onPushed: (b, mainId) => {
        playPushLive();
        if (stayAfterPush) return;
        navigate(routes.space.workflow, {
          spaceId,
          workflowId: b.workflowId,
          branchId: mainId ?? "",
        });
      },
      onPushFailed: (b, error) => {
        // Main moved between the conflict check and the push; pull the new
        // changes in so resolution starts from them.
        const conflict = error.details?.code === "behind-with-conflicts";
        if (conflict) {
          mergeMain();
          showToast({
            icon: "circle-off",
            message:
              "Conflicts need to be resolved before pushing live. Main’s changes were pulled into this draft.",
            buttonLabel: "Dismiss",
          });
        } else if (stayAfterPush) {
          showToast({
            icon: "circle-off",
            message: error.message,
            buttonLabel: "Dismiss",
          });
        }
        if (stayAfterPush) return;
        navigate(routes.space.workflow, {
          spaceId,
          workflowId: b.workflowId,
          branchId: b.id,
        });
      },
    });
    if (result) {
      onDeploying?.(true);
      void result.finally(() => onDeploying?.(false));
    }
    return result;
  };

  return { pushLive, mergeMain };
}
